import { captureException } from '@sentry/react'
import {
  enums,
  invalidateQueriesContainingPattern,
  schemas,
  useClient,
  useQuery
} from '@weenat/client'
import { InvitationStatus } from '@weenat/client/dist/enums'
import { PossibleError } from '@weenat/client/dist/errors'
import { RequestType } from '@weenat/client/dist/resources/types'
import * as zodSchemas from '@weenat/client/dist/zod-schemas'
import { useIntl } from '@weenat/wintl'
import { useNavigate, useSearchParams } from 'app/routx-router'
import AuthCard from 'app/src/authentication/components/AuthCard'
import GoToLoginWithEmail from 'app/src/authentication/components/GoToLoginWithEmail'
import useToasts from 'app/src/hooks/useToasts'
import useWishedNetworkFromUrl from 'app/src/hooks/useWishedNetworkFromUrl'
import DelimitedFlex from 'app/src/kit/DelimitedFlex'
import SubmitButton from 'app/src/kit/SubmitButton'
import SuperForm from 'app/src/kit/SuperForm'
import Text from 'app/src/kit/Text'
import CountryPickerField from 'app/src/kit/fields/CountryPickerField'
import EmailField from 'app/src/kit/fields/EmailField'
import FirstNameField from 'app/src/kit/fields/FirstNameField'
import HasAcceptedTermsOfServiceField from 'app/src/kit/fields/HasAcceptedTermsOfServiceField'
import LastNameField from 'app/src/kit/fields/LastNameField'
import PasswordField from 'app/src/kit/fields/PasswordField'
import TelephoneFields from 'app/src/kit/fields/TelephoneFields'
import { useToken } from 'app/state'
import { logNavigateOnRegistration } from 'app/utils/analytics'
import { FormikProps, useFormikContext } from 'formik'
import isEmpty from 'lodash-es/isEmpty'
import isNil from 'lodash-es/isNil'
import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'

export const settings = {
  search: z.object({
    ...zodSchemas.email.shape,
    ...zodSchemas.wishedNetwork.shape,
    ...zodSchemas.knownUtmParams.shape,
    ...zodSchemas.emailInvitation.pick({
      firstName: true,
      lastName: true,
      country: true,
      type: true,
      role: true,
      orgName: true,
      networkName: true
    }).shape,
    phoneCountry: z.string().optional(),
    phoneNumber: z.string().optional()
  })
}

const REGISTER_FORM_SCHEMA = schemas.auth.register
type RegisterFormValues = typeof REGISTER_FORM_SCHEMA.initialValues

const RegisterFields = ({
  isLoading,
  errorState
}: {
  isLoading: boolean
  errorState: PossibleError | null
}) => {
  const { values, setFieldValue } = useFormikContext<RegisterFormValues>()

  const sharedFieldsProps = { $isDisabled: isLoading, errorState }

  useEffect(() => {
    if (values.country) {
      setFieldValue('telephoneCountryCode', values.country)
    }
  }, [values.country])

  return (
    <>
      <Flex $gap='md'>
        <Box $flex={1}>
          <FirstNameField {...sharedFieldsProps} />
        </Box>
        <Box $flex={1}>
          <LastNameField {...sharedFieldsProps} />
        </Box>
      </Flex>
      <EmailField {...sharedFieldsProps} />
      <PasswordField {...sharedFieldsProps} />
      <CountryPickerField name='country' />
      <TelephoneFields {...sharedFieldsProps} errorPath='telephoneNumber' />
      <Box $width='100%' $mt='lg'>
        <HasAcceptedTermsOfServiceField {...sharedFieldsProps} />
      </Box>
    </>
  )
}

const Register = () => {
  const { t } = useIntl()
  const client = useClient()
  const [searchParams] = useSearchParams(settings.search)
  const { addErrorToast } = useToasts()
  const [, setToken] = useToken()
  const nav = useNavigate()
  const [registerState, setRegisterState] = useState<'loading' | 'success' | 'error' | 'idle'>(
    'idle'
  )
  const [registerError, setRegisterError] = useState<PossibleError | null>(null)

  const {
    type,
    firstName,
    lastName,
    country,
    role,
    networkName: invitationNetworkName,
    orgName,
    phoneCountry,
    phoneNumber
  } = searchParams
  const hasEmailInvitation = !isNil(type)

  const formRef = useRef<FormikProps<RegisterFormValues>>(null)

  const wishedNetwork = useWishedNetworkFromUrl() || searchParams?.wishedNetwork

  const networkRequest = useQuery(client.networks.get(wishedNetwork?.networkId), {
    enabled: !isNil(wishedNetwork?.networkId)
  })

  const networkName = !isNil(networkRequest.data) ? networkRequest.data.name : undefined

  const startOnboardingWithWishedNetwork = () => {
    nav(`/verify-email/waiting`, {
      search: {
        networkName
      }
    })
  }

  const startOnboardingWithAcceptedInvitation = () => {
    nav(`/verify-email/waiting`, {
      search: {
        type,
        networkName: invitationNetworkName,
        orgName,
        role
      }
    })
  }

  const startClassicOnboarding = () => {
    nav(`/verify-email/waiting`)
  }

  /** We want the email from the state (to handle login/register switch with same email), not from the emailInvitation */
  const email = !isNil(searchParams?.email) ? searchParams?.email : ''

  const initialValues = useMemo(
    () => ({
      email,
      firstName: isNil(firstName) ? '' : firstName,
      lastName: isNil(lastName) ? '' : lastName,
      country: isNil(country) ? 'FR' : country,
      telephoneCountryCode: isNil(phoneCountry) ? 'FR' : phoneCountry,
      telephoneNumber: isNil(phoneNumber) ? '' : phoneNumber
    }),
    [country, email, firstName, lastName, phoneCountry, phoneNumber]
  )

  const onSubmit = async (values: RegisterFormValues) => {
    const { telephoneCountryCode, telephoneNumber, ...valuesWithoutPhone } = values

    try {
      setRegisterState('loading')
      const registerResult = await client.auth
        .register({
          utm_campaign: searchParams.utm_campaign,
          utm_source: searchParams.utm_source,
          utm_medium: searchParams.utm_medium,
          utm_term: searchParams.utm_term,
          utm_content: searchParams.utm_content
        })
        .request(!isEmpty(telephoneNumber.trim()) ? values : valuesWithoutPhone)

      setToken(registerResult.token)

      try {
        const ownedOrgs = await client.orgs.getAllPages({ myRole: enums.OrgRole.owner }).request()
        const firstOwnedOrg = ownedOrgs.at(0)

        if (!isNil(firstOwnedOrg) && !isNil(firstOwnedOrg.id)) {
          if (hasEmailInvitation) {
            try {
              if (type == RequestType.adherentFarmInvitationWithTrial) {
                const { results, count } = await client.invitations.received
                  .getMany({
                    status: InvitationStatus.pending,
                    limit: 1
                  })
                  .request()
                const invitation = results.at(0)
                if (count === 1 && !isNil(invitation)) {
                  await client.invitations.received
                    .accept(invitation.id)
                    .request({ organizationId: firstOwnedOrg.id })
                }
                invalidateQueriesContainingPattern('')
              }
            } catch (err) {
              captureException(err)
            }
            setRegisterState('success')
            await client.preferences.update().request({
              onboardingContext: {
                shouldShowOnboarding: true,
                search: {
                  type,
                  networkName: invitationNetworkName,
                  orgName,
                  role
                },
                step: null
              }
            })
            startOnboardingWithAcceptedInvitation()
          } else if (wishedNetwork) {
            await client.invitations.beg.networkMembership(wishedNetwork?.networkId).request({
              organizationId: firstOwnedOrg.id,
              trial: wishedNetwork.trial
            })

            setRegisterState('success')
            await client.preferences.update().request({
              onboardingContext: {
                shouldShowOnboarding: true,
                search: {
                  networkName
                },
                step: null
              }
            })
            startOnboardingWithWishedNetwork()
          } else {
            setRegisterState('success')
            await client.preferences.update().request({
              onboardingContext: {
                shouldShowOnboarding: true,
                search: {},
                step: null
              }
            })
            startClassicOnboarding()
          }
        }
      } catch {
        setRegisterState('error')
      }
    } catch (e) {
      setRegisterState('error')
      setRegisterError(e)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const emailError: string | undefined = e.fieldErrors?.email?.[0]?.message
      if (!isNil(emailError)) addErrorToast(emailError)
    }
  }

  useEffect(() => {
    logNavigateOnRegistration()
  }, [])

  return (
    <AuthCard>
      <Box>
        <Text $fontWeight='bold' $fontSize='md' $textAlign='center'>
          {t('models.account.actions.register')}
        </Text>
        <SuperForm
          schema={REGISTER_FORM_SCHEMA}
          onSubmit={onSubmit}
          initialValues={initialValues}
          formRef={formRef}
        >
          <RegisterFields isLoading={registerState === 'loading'} errorState={registerError} />
          <SubmitButton
            style={{ width: '100%' }}
            isDisabled={registerState === 'loading'}
            isLoading={registerState === 'loading'}
            isError={registerState === 'error'}
            isSuccess={registerState === 'success'}
          >
            {t('models.account.actions.register')}
          </SubmitButton>
        </SuperForm>

        <DelimitedFlex
          $isDelimitedOnTop
          $alignItems='stretch'
          $justifyContent='stretch'
          $pt='lg'
          $mt='lg'
        >
          <GoToLoginWithEmail
            style={{ width: '100%' }}
            isDisabled={registerState === 'loading'}
            email={
              !isNil(formRef) && !isNil(formRef.current) ? formRef.current.values.email : undefined
            }
            wishedNetwork={
              !isNil(wishedNetwork)
                ? {
                    ...wishedNetwork,
                    networkName: networkName as string
                  }
                : undefined
            }
          />
        </DelimitedFlex>
      </Box>
    </AuthCard>
  )
}

export default Register
